import { ServiceType } from '@wix/ambassador-bookings-services-v2-service/types';
import { bookingsCantBookGroupsMessage } from '@wix/bi-logger-wixboost-ugc/v2';
import {
  isServiceWithAddOns,
  isServiceWithDynamicDuration,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import { shouldOpenPreferencesModal } from '@wix/bookings-services-preferences-modal/utils';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { BookingsAPI } from '../../api/BookingsApi';
import { ServiceListContext } from '../../context/createServiceListContext';
import { EnrichedService } from '../../types/types';
import { ReferralInfo } from '../../utils/bi/consts';
import { BlockNavigationReason } from '../../utils/services/getBlockNavigationReason/getBlockNavigationReason';
import { NavigationTarget } from '../navigateToNextPage/consts';
import { NavigateToNextPageAction } from '../navigateToNextPage/navigateToNextPage';
import { OpenPremiumDialogAction } from '../openPremiumDialog/openPremiumDialog';
import { openServicesPreferencesModalAction } from '../openServicesPreferencesModal/openServicesPreferencesModal';

export type OnBookFlowStartAttemptAction = ({
  service,
  referralInfo,
}: {
  service: EnrichedService;
  referralInfo: ReferralInfo;
}) => Promise<void>;

const deprecatedOpenPreferencesModalCondition = ({
  service,
  isMultiServiceAppointmentEnabled,
  flowAPI,
}: {
  service: EnrichedService;
  isMultiServiceAppointmentEnabled: boolean;
  flowAPI: ControllerFlowAPI;
}): boolean => {
  const isAppointmentService = service.type === ServiceType.APPOINTMENT;

  const isMultiServicesAppointmentsEnable =
    !service.dummy && isAppointmentService && isMultiServiceAppointmentEnabled;

  const isServicePreferencesAppointmentsEnable =
    isServiceWithDynamicDuration(service);

  const isAppointmentWithAddOns =
    isAppointmentService && isServiceWithAddOns(service, flowAPI.experiments);

  return (
    isMultiServicesAppointmentsEnable ||
    isServicePreferencesAppointmentsEnable ||
    isAppointmentWithAddOns
  );
};

export const createOnBookFlowStartAttemptAction = ({
  flowAPI,
  bookingsApi,
  openServicesPreferencesModal,
  navigateToNextPage,
  openPremiumDialog,
  serviceListContext,
}: {
  flowAPI: ControllerFlowAPI;
  bookingsApi: BookingsAPI;
  openServicesPreferencesModal: openServicesPreferencesModalAction;
  openPremiumDialog: OpenPremiumDialogAction;
  navigateToNextPage: NavigateToNextPageAction;
  serviceListContext: ServiceListContext;
}): OnBookFlowStartAttemptAction => {
  const { isPricingPlanInstalled, isMultiServiceAppointmentEnabled } =
    serviceListContext;

  return async ({ service, referralInfo }) => {
    if (service.blockNavigationReason) {
      if (service.blockNavigationReason === BlockNavigationReason.NOT_PREMIUM) {
        bookingsApi.notifyOwnerNonPremiumEnrollmentAttempt({
          service,
        });
      } else if (
        service.blockNavigationReason ===
        BlockNavigationReason.PRICING_PLAN_ERROR
      ) {
        bookingsApi.notifyOwnerNonPricingPlanEnrollmentAttempt({
          isPricingPlanInstalled,
          service,
        });
      }
      flowAPI.bi?.report(
        bookingsCantBookGroupsMessage({
          referralInfo: ReferralInfo.WIDGET,
        }),
      );

      openPremiumDialog();
    } else {
      const isBookFlowSettingsEnabled = flowAPI.experiments.enabled(
        'specs.bookings.BookFlowSettings',
      );

      let openPreferencesModal: boolean;

      if (isBookFlowSettingsEnabled) {
        openPreferencesModal = shouldOpenPreferencesModal({
          service,
          isDummy: service.dummy,
          experiments: flowAPI.experiments,
          isMultiServiceAppointmentEnabled,
        });
      } else {
        openPreferencesModal = deprecatedOpenPreferencesModalCondition({
          service,
          isMultiServiceAppointmentEnabled,
          flowAPI,
        });
      }

      if (openPreferencesModal) {
        openServicesPreferencesModal({ service });
      } else {
        return navigateToNextPage({
          service,
          referralInfo,
          target: NavigationTarget.CALENDAR_PAGE,
        });
      }
    }
  };
};
